import React, {useEffect, useState, memo, useCallback, useLayoutEffect, Suspense, startTransition} from "react"
import {useTemplatedComponent} from "@next/templating"
import {MANIFEST} from "@manifest/header"
import {IncomingHttpHeaders} from "http"
import {getSeoContent} from "../api/seo"
import connect from "./connect"
import PublishCountrySelectorClosed from "../events/countrySelectorClosed"
import cleanupPersonalisationData from "../utils/cleanupPersonalisationData"
import {getRealTimePersonalisationData} from "../utils/realTimePersonalisation"
import useBloomreachCookieCheck from "../hooks/useBloomreachCookieCheck"
import {usePushBloomreachDetails} from "../hooks/useTrackingEvents"
import useSetSegmentCookie from "../hooks/useSetSegmentCookie"
import {EventComponent} from "./eventsComponent"

declare global {
    interface Window {
        dataLayer: any[]
        branch: any
        cloudHeader: any
    }
}

export interface HeaderProps {
    bloomReachCachingCookieList: string
    bloomReachCachingEnabled: boolean
    cookieDomain: string
    enableCookieConsent: boolean
    enableFavourites: boolean
    geolocationUrl: string
    geolocationVersion: number
    isBloomreachCookieFullDomain: boolean
    itemCount: number
    privacyUrl: string
    requestCountryChange: () => void
    requestedCountryChange: boolean
    showCheckoutButton: boolean
    showSaleWarningBag: boolean
    showStaticHeaderBanner: boolean
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    territory: string
    textAlignment: string
    useBloomreachSegmentCookie: boolean
    useDevEsi: boolean
    widescreenCookieConsent: boolean
    updatedmasid: boolean
    realm: string
    language: string
    displayTestTools: boolean
    enableContentBanner: boolean
    tabbedNavigationEnabled: boolean
    branchIOEnabled: boolean
    seoClientSidePixelEnabled: boolean
    firstName: string
    headers: IncomingHttpHeaders
    showSignoutButton: boolean
    showMyAccountIcon: boolean
    cleanSiteUrl: string
    tpLitePath: string
    personalisationDataRetrievalEnabled: boolean
    personalisationDataRetrievalAPIVersion: string
    seoClientSidePixelUseEffectEnabled: boolean
    isSecure: boolean
    doCountrySelectorRedirect: boolean
    countrySelectorEndpoint: string
    xxlBreakpointEnabled: boolean
    enableMinibagNotification: boolean
    personalisationSessionDataExpirationTime: number
    xxlBreakpointMaxWidth: number
}

export const Header = memo(
    ({
        bloomReachCachingCookieList,
        bloomReachCachingEnabled,
        cookieDomain,
        enableCookieConsent,
        enableFavourites,
        geolocationUrl,
        geolocationVersion,
        isBloomreachCookieFullDomain,
        itemCount,
        privacyUrl,
        requestCountryChange,
        requestedCountryChange,
        showCheckoutButton,
        showSaleWarningBag,
        showStaticHeaderBanner,
        templating,
        territory,
        textAlignment,
        useBloomreachSegmentCookie,
        useDevEsi,
        widescreenCookieConsent,
        updatedmasid,
        realm,
        language,
        displayTestTools,
        enableContentBanner,
        tabbedNavigationEnabled,
        branchIOEnabled,
        seoClientSidePixelEnabled,
        firstName,
        headers,
        showSignoutButton,
        showMyAccountIcon,
        cleanSiteUrl,
        personalisationDataRetrievalEnabled,
        personalisationDataRetrievalAPIVersion,
        isSecure,
        tpLitePath,
        seoClientSidePixelUseEffectEnabled,
        doCountrySelectorRedirect,
        countrySelectorEndpoint,
        xxlBreakpointEnabled,
        enableMinibagNotification,
        personalisationSessionDataExpirationTime,
        xxlBreakpointMaxWidth,
    }: HeaderProps) => {
        useEffect(() => {
            cleanupPersonalisationData(personalisationSessionDataExpirationTime, () => {
                getRealTimePersonalisationData(
                    personalisationDataRetrievalEnabled,
                    personalisationDataRetrievalAPIVersion,
                    realm,
                    territory,
                    language,
                    cleanSiteUrl,
                    false, // isAnonymousUser user is set to false to make sure personalisation data is fetched as a known users when making the early call
                    !enableCookieConsent,
                )
            })
        }, [])

        const [showModal, setShowModal] = useState(false)
        const [localFirstName, setLocalFirstName] = useState(firstName)
        const [branchBannerHeight, setBranchBannerHeight] = useState(0)
        const [seoHtml, setSeoHtml] = useState("")
        const [shouldRenderSeoLinks, setShouldRenderSeoLinks] = useState(false)
        const isClient = typeof window !== "undefined"

        useSetSegmentCookie(useBloomreachSegmentCookie)

        useEffect(() => {
            if (itemCount > 0 && requestedCountryChange) {
                PublishCountrySelectorClosed()
                setShowModal(true)
            }
        }, [itemCount, requestedCountryChange])

        // Branch IO
        useEffect(() => {
            if (branchIOEnabled) {
                const initialListener = function (event, data) {
                    // Set Header Height
                    setBranchBannerHeight(data.bannerHeight)

                    const heightFromEvent = data.bannerHeight || "0"
                    if (heightFromEvent) {
                        const processedHeight = heightFromEvent.replace("px", "")
                        const currentVal = window
                            .getComputedStyle(document.body)
                            .getPropertyValue("--next-header-scroll-height")
                            .replace("rem", "")
                        const newHeight = Number.parseFloat(currentVal) + Number.parseFloat(processedHeight) / 16
                        document.documentElement.style.setProperty("--next-header-scroll-height", `${newHeight}rem`)
                    }
                }

                // Work around event listener to update the body margin-top property as it is being set by the banner once it
                // loads in for certain clients
                const bannerOpenListener = function () {
                    document.body.style.marginTop = "0"
                    const branchIframe = document.getElementById("branch-banner-iframe")
                    // zIndex value of 1030 used to stop banner overlapping popups, exising value used in MVC solution for branchIO
                    if (branchIframe !== undefined && branchIframe !== null) branchIframe.style.zIndex = "1030"
                }

                const closeListener = function () {
                    setBranchBannerHeight(0)
                    document.documentElement.style.removeProperty("--next-header-scroll-height")
                    document.body.style.marginTop = "0"
                }

                // Add the event listner first
                window.addEventListener("BRANCHIO_INIT", () => {
                    if (window.branch !== undefined) {
                        window.branch.addListener("willShowJourney", initialListener)
                        window.branch.addListener("didCloseJourney", closeListener)
                        window.branch.addListener("didShowJourney", bannerOpenListener)
                    }
                })

                // Notify GTM that header is ready; GTM will then trigger the BRANCHIO_INIT event,
                // for us to listen for.
                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({event: "cloud-header-branch-io-ready"})
            }
        }, [])

        const closeModalHandler = useCallback(() => {
            requestCountryChange()
            setShowModal(false)
        }, [requestCountryChange])

        // SEO Client Side Pixel - only run on client side to prevent hydration issues
        useEffect(() => {
            if (!isClient || isSecure || !seoClientSidePixelEnabled || !seoClientSidePixelUseEffectEnabled) {
                return
            }

            // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-floating-promises
            ;(async () => {
                const data = await getSeoContent(cleanSiteUrl, headers, tpLitePath)
                setSeoHtml(data)
            })()
        }, [
            isClient,
            isSecure,
            seoClientSidePixelEnabled,
            seoClientSidePixelUseEffectEnabled,
            cleanSiteUrl,
            headers,
            tpLitePath,
        ])

        useLayoutEffect(() => {
            if (!isClient || isSecure || !seoClientSidePixelEnabled || seoClientSidePixelUseEffectEnabled) {
                return
            }

            // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-floating-promises
            ;(async () => {
                const data = await getSeoContent(cleanSiteUrl, headers, tpLitePath)
                setSeoHtml(data)
            })()
        }, [
            isClient,
            isSecure,
            seoClientSidePixelEnabled,
            seoClientSidePixelUseEffectEnabled,
            cleanSiteUrl,
            headers,
            tpLitePath,
        ])

        useEffect(() => {
            if (isClient && seoClientSidePixelEnabled && !isSecure) {
                startTransition(() => {
                    setShouldRenderSeoLinks(true)
                })
            }
        }, [isClient, seoClientSidePixelEnabled, isSecure])

        const renderSEOLinks = () => {
            return {__html: seoHtml}
        }

        const bloomreachCookieDomain = isBloomreachCookieFullDomain ? "" : cookieDomain

        useBloomreachCookieCheck({
            cookieDomain: bloomreachCookieDomain,
            bloomReachCachingCookieList,
            bloomReachCachingEnabled,
        })
        usePushBloomreachDetails()

        const TemplateComponent = useTemplatedComponent(templating, MANIFEST)

        return (
            <>
                {branchBannerHeight ? (
                    <style>
                        {`
                    :root {
                        --next-header-branchio-height: ${branchBannerHeight};
                    }
                    `}
                    </style>
                ) : null}
                <Suspense>
                    <TemplateComponent
                        closeModalHandler={closeModalHandler}
                        enableCookieConsent={enableCookieConsent}
                        privacyUrl={privacyUrl}
                        showCheckoutButton={showCheckoutButton}
                        showModal={showModal}
                        showSaleWarningBag={showSaleWarningBag}
                        showStaticHeaderBanner={showStaticHeaderBanner}
                        textAlignment={textAlignment}
                        useDevEsi={useDevEsi}
                        widescreenCookieConsent={widescreenCookieConsent}
                        displayTestTools={displayTestTools}
                        enableContentBanner={enableContentBanner}
                        tabbedNavigationEnabled={tabbedNavigationEnabled}
                        showSignoutButton={showSignoutButton}
                        showMyAccountIcon={showMyAccountIcon}
                        key={1}
                        isSecure={isSecure}
                        xxlBreakpointEnabled={xxlBreakpointEnabled}
                        xxlBreakpointMaxWidth={xxlBreakpointMaxWidth}
                    />
                </Suspense>
                <Suspense>
                    <EventComponent
                        enableFavourites={enableFavourites}
                        siteUrl={cleanSiteUrl}
                        updatedmasid={updatedmasid}
                        realm={realm}
                        territory={territory}
                        language={language}
                        geolocationUrl={geolocationUrl}
                        geolocationVersion={geolocationVersion}
                        firstName={firstName}
                        {...{
                            cleanSiteUrl,
                            personalisationDataRetrievalEnabled,
                            personalisationDataRetrievalAPIVersion,
                            doCountrySelectorRedirect,
                            countrySelectorEndpoint,
                        }}
                        enableMinibagNotification={enableMinibagNotification}
                        enableCookieConsent={enableCookieConsent}
                    />
                </Suspense>

                <div
                    id="embeddedNavLinks"
                    style={{height: "0px", width: "100%", overflow: "hidden", position: "fixed"}}
                    // eslint-disable-next-line react/no-danger
                    {...(shouldRenderSeoLinks && seoHtml !== "" ? {dangerouslySetInnerHTML: renderSEOLinks()} : {})}
                />
            </>
        )
    },
)

Header.displayName = "Header"
export default connect(Header)
